import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const imgUrl = (u) => (!u ? "" : u.startsWith("/api") ? `${BACKEND_URL}${u}` : u);

const TOKEN_KEY = "maya_admin_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

export const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const errMsg = (e, fb = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return fb;
};

export const fetchSite = () => axios.get(`${API}/site`).then((r) => r.data);

const CHUNK = 1024 * 1024;
/** Chunked upload. onProgress(0-100). Returns stored url. */
export async function uploadImage(file, onProgress = () => {}) {
  const { data } = await adminApi.post("/admin/uploads/init", { filename: file.name, size: file.size });
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("index", String(i));
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    await adminApi.post(`/admin/uploads/${data.upload_id}/chunk`, fd);
    onProgress(Math.round(((i + 1) / total) * 90));
  }
  const res = await adminApi.post(`/admin/uploads/${data.upload_id}/complete`, { total_chunks: total });
  onProgress(100);
  return res.data.url;
}

/* ---------- Member (fan) API ---------- */
const MEMBER_KEY = "maya_member_token";
export const getMemberToken = () => localStorage.getItem(MEMBER_KEY);
export const setMemberToken = (t) => (t ? localStorage.setItem(MEMBER_KEY, t) : localStorage.removeItem(MEMBER_KEY));

export const memberApi = axios.create({ baseURL: API });
memberApi.interceptors.request.use((cfg) => {
  const t = getMemberToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

/** Streams Maya's reply. Calls onEvent({type, ...}) for each SSE event. */
export async function streamChat(text, onEvent) {
  const res = await fetch(`${API}/member/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getMemberToken()}` },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    let detail = "Message failed";
    try { detail = (await res.json()).detail || detail; } catch (e) { /* ignore */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.split("\n").find((l) => l.startsWith("data: "));
      if (line) { try { onEvent(JSON.parse(line.slice(6))); } catch (e) { /* ignore */ } }
    }
  }
}

/** Private (VIP) media URL with token in query, since <video> can't send headers. */
export const mediaUrl = (u, token) => (u ? `${BACKEND_URL}${u}?token=${encodeURIComponent(token || "")}` : "");

/** Grab a JPEG thumbnail + duration from a local video file in the browser. */
export function captureVideoThumb(file, at = 1) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata"; v.muted = true; v.playsInline = true; v.src = url;
    const fail = () => { URL.revokeObjectURL(url); resolve({ thumb: null, duration: null }); };
    v.onerror = fail;
    v.onloadedmetadata = () => { v.currentTime = Math.min(at, (v.duration || 2) / 2); };
    v.onseeked = () => {
      try {
        const c = document.createElement("canvas");
        const w = Math.min(720, v.videoWidth || 720);
        c.width = w; c.height = Math.round(((v.videoHeight || 405) / (v.videoWidth || 720)) * w);
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        c.toBlob((b) => {
          URL.revokeObjectURL(url);
          resolve({ thumb: b ? new File([b], `${file.name.replace(/\.[^.]+$/, "")}-thumb.jpg`, { type: "image/jpeg" }) : null, duration: v.duration || null });
        }, "image/jpeg", 0.85);
      } catch (e) { fail(); }
    };
    setTimeout(fail, 15000);
  });
}

export const fmtDuration = (s) => {
  if (!s && s !== 0) return "";
  const m = Math.floor(s / 60); const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};
